import { useEffect, useState } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";

// Вспомогательная функция для поиска ближайшего срока
const getNearestDueDate = (tasks) => {
  const dates = tasks
    .filter(t => t.status === "in_progress" && t.due_date)
    .map(t => new Date(t.due_date))
    .filter(d => !isNaN(d));
  if (!dates.length) return null;
  return dates.reduce((min, d) => (d < min ? d : min), dates[0]);
};
const getNearestTask = (tasks) => {
  const inProgress = tasks
    .filter(t => t.status === "in_progress" && t.due_date)
    .map(t => ({ ...t, due: new Date(t.due_date) }))
    .filter(t => !isNaN(t.due));
  if (!inProgress.length) return null;
  return inProgress.reduce((min, t) => t.due < min.due ? t : min, inProgress[0]);
};

export default function TenderListPage() {
  const [tenders, setTenders] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [newTender, setNewTender] = useState({
    number: "", title: "", customer: "", bid_amount: "", cost: "", contact: "", project_path: ""
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const navigate = useNavigate();

  // Загрузка всех тендеров
  useEffect(() => {
    api.get("/tenders/")
      .then(res => setTenders(res.data))
      .catch(() => setError("Не удалось загрузить тендеры."));
  }, []);

  // Экспорт в Excel
  const handleExportExcel = async () => {
    try {
      const response = await api.get("/tenders/export_excel/", { responseType: "blob" });
      const disposition = response.headers["content-disposition"];
      let filename = "tenders.xlsx";
      if (disposition?.includes("filename=")) {
        filename = decodeURIComponent(disposition.split("filename=")[1].replace(/\"/g, ""));
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Ошибка при экспорте");
      console.error(err);
    }
  };
  const handleGlobalSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await api.get(`/tenders/?search=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error(err);
      alert("Ошибка при поиске");
    }
  };
  const resetSearch = () => {
    setSearchResults(null);
    setSearchQuery("");
  };
  // Отфильтровать тендеры по строке поиска
  const filterBySearch = t => {
    const q = searchTerm.toLowerCase();
    return (
      t.number?.toLowerCase().includes(q) ||
      t.title?.toLowerCase().includes(q) ||
      t.customer?.toLowerCase().includes(q) ||
      t.contact?.toLowerCase().includes(q)
    );
  };

  // Разбивка на активные и архивные
  const activeAll = [...tenders]
    .filter(t => t.status === "active")
    .sort((a, b) => {
      const da = getNearestDueDate(a.tasks) || Infinity;
      const db = getNearestDueDate(b.tasks) || Infinity;
      return da - db;
  });

  const active = searchTerm ? activeAll.filter(filterBySearch) : activeAll;

// ✅ архив — сортировка по last_done_date
const archivedAll = [...tenders]
    .filter(t => t.status === "archived")
    .sort((a, b) => {
      const da = a.last_done_date ? new Date(a.last_done_date) : 0;
      const db = b.last_done_date ? new Date(b.last_done_date) : 0;
      return db - da; // по убыванию
  })
  .slice(0, 5);

const archived = searchTerm ? archivedAll.filter(filterBySearch) : archivedAll;

  return (
    <div className="p-6">
      {/* Заголовок + поиск + кнопки */}
<div className="flex justify-between items-center mb-4">
  <h1 className="text-2xl font-bold">В работе</h1>
  <div className="flex gap-2">
    <input
      type="text"
      value={searchQuery}
      onChange={e => setSearchQuery(e.target.value)}
      placeholder="Поиск..."
      className="border px-2 py-1 rounded"
    />
    <button
      onClick={handleGlobalSearch}
      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
    >
      🔍 Найти
    </button>
    {searchResults !== null && (
      <button
        onClick={resetSearch}
        className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
      >
        ✖️ Сброс
      </button>
    )}
    <button
      onClick={handleExportExcel}
      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
    >
      📥 Экспорт
    </button>
    <button
      onClick={() => setShowForm(!showForm)}
      className="bg-slate-600 text-white px-4 py-2 rounded hover:bg-slate-700"
    >
      ➕ Добавить тендер
    </button>
  </div>
</div>


      {/* Форма создания */}
      {showForm && (
        <div className="grid grid-cols-2 gap-4 border p-4 mb-6 bg-gray-50 rounded">
          {[
            { key: "number", label: "Номер" },
            { key: "title", label: "Наименование" },
            { key: "customer", label: "Заказчик" },
            { key: "bid_amount", label: "Сумма заявки" },
            { key: "cost", label: "Себестоимость" },
            { key: "contact", label: "Контакт" },
            { key: "project_path", label: "Путь к папке" },
          ].map(({ key, label }) => (
            <input
              key={key}
              className="border px-2 py-1 rounded"
              placeholder={label}
              value={newTender[key] || ""}
              onChange={e => setNewTender(prev => ({ ...prev, [key]: e.target.value }))}
            />
          ))}
          <div className="col-span-2 flex gap-4">
            <button
              onClick={() => {
                const payload = {
                  ...newTender,
                  bid_amount: parseFloat(newTender.bid_amount) || null,
                  cost: parseFloat(newTender.cost) || null,
                };
                api.post("/tenders/", payload)
                  .then(res => setTenders(prev => [res.data, ...prev]))
                  .catch(() => alert("Не удалось создать тендер"))
                  .finally(() => setShowForm(false));
              }}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              💾 Сохранить
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-red-500 mb-4">{error}</p>}

      { /* Если есть результаты глобального поиска — показываем их одним списком */ }
{searchResults !== null ? (
  <TenderTable
    tenders={searchResults}
    onRowClick={id => navigate(`/tenders/${id}`)}
  />
) : (
  <>
    { /* Таблица активных */ }
    <TenderTable
      tenders={active}
      onRowClick={id => navigate(`/tenders/${id}`)}
    />

    { /* Архив */ }
    <h2 className="text-2xl font-bold text-center my-6">Архив</h2>
    <TenderTable
      tenders={archived}
      onRowClick={id => navigate(`/tenders/${id}`)}
    />

    { /* Кнопка «Перейти в архив» (если есть хоть один архивный тендер и не в режиме поиска) */ }
    {archivedAll.length > 0 && searchResults === null && (
      <div className="mt-4 text-center">
        <button
          onClick={() => navigate("/tenders/archive")}
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
        >
          Перейти в архив
        </button>
      </div>
    )}
  </>
)}
    </div>
  );
}

function TenderTable({ tenders, onRowClick }) {
  if (!tenders.length) {
    return <p className="text-gray-600">Нет тендеров для отображения.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1000px] table-fixed border-collapse">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-2 w-12 text-center">№</th>
            <th className="p-2">Номер</th>
            <th className="p-2">Заказчик</th>
            <th className="p-2">Наименование</th>
            <th className="p-2">Сумма заявки</th>
            <th className="p-2">Исполнитель</th>
            <th className="p-2">Срок</th>
            <th className="p-2">Обновлено</th>
          </tr>
        </thead>
        <tbody>
          {tenders.map((t, idx) => {
            const nearestTask = getNearestTask(t.tasks);
            return (
              <tr
                key={t.id}
                onClick={() => onRowClick(t.id)}
                className="border-t hover:bg-gray-100 cursor-pointer"
              >
                <td className="p-2 w-12 text-center">{idx + 1}</td>
                <td className="p-2">{t.number}</td>
                <td className="p-2">{t.customer}</td>
                <td className="p-2">{t.title}</td>
                <td className="p-2">
                  {t.bid_amount != null
                    ? parseFloat(t.bid_amount).toLocaleString("ru-RU", {
                        style: "currency",
                        currency: "RUB",
                      })
                    : "—"}
                </td>
                <td className="p-2">{t.executor?.full_name || "—"}</td>
                <td className="p-2">
                   {nearestTask ? (
          <>
            <div>{nearestTask.due.toLocaleDateString()}</div>
            <div className="text-sm text-gray-500">{nearestTask.title}</div>
          </>
        ) : "—"}
      </td>
                <td className="p-2">
                  {new Date(t.updated_at).toLocaleDateString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
